import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../api';
import { getMyCompanies, createCompany, updateCompany, deleteCompany, getProjects } from '../api';

const NAICS_MAP = {
  'Technology':                  {
    naics: { code: '541511', title: 'Custom Computer Programming Services', sector: '54 - Professional, Scientific & Technical Services' },
    sic:   { code: '7371',   title: 'Computer Programming Services' },
  },
  'Marketing & Advertising':     {
    naics: { code: '541810', title: 'Advertising Agencies', sector: '54 - Professional, Scientific & Technical Services' },
    sic:   { code: '7311',   title: 'Advertising Agencies' },
  },
  'Consulting':                  {
    naics: { code: '541611', title: 'Administrative & General Management Consulting', sector: '54 - Professional, Scientific & Technical Services' },
    sic:   { code: '8742',   title: 'Management Consulting Services' },
  },
  'Finance & Accounting':        {
    naics: { code: '541211', title: 'Offices of Certified Public Accountants', sector: '54 - Professional, Scientific & Technical Services' },
    sic:   { code: '8721',   title: 'Accounting, Auditing & Bookkeeping' },
  },
  'Healthcare':                  {
    naics: { code: '621111', title: 'Offices of Physicians', sector: '62 - Health Care & Social Assistance' },
    sic:   { code: '8011',   title: 'Offices & Clinics of Medical Doctors' },
  },
  'Legal':                       {
    naics: { code: '541110', title: 'Offices of Lawyers', sector: '54 - Professional, Scientific & Technical Services' },
    sic:   { code: '8111',   title: 'Legal Services' },
  },
  'Real Estate':                 {
    naics: { code: '531210', title: 'Offices of Real Estate Agents & Brokers', sector: '53 - Real Estate & Rental & Leasing' },
    sic:   { code: '6531',   title: 'Real Estate Agents & Managers' },
  },
  'Construction':                {
    naics: { code: '236220', title: 'Commercial & Institutional Building Construction', sector: '23 - Construction' },
    sic:   { code: '1542',   title: 'General Contractors — Nonresidential Buildings' },
  },
  'Education':                   {
    naics: { code: '611710', title: 'Educational Support Services', sector: '61 - Educational Services' },
    sic:   { code: '8299',   title: 'Schools & Educational Services, NEC' },
  },
  'Non-Profit':                  {
    naics: { code: '813319', title: 'Other Social Advocacy Organizations', sector: '81 - Other Services' },
    sic:   { code: '8399',   title: 'Social Services, NEC' },
  },
  'Retail':                      {
    naics: { code: '452990', title: 'All Other General Merchandise Retailers', sector: '44-45 - Retail Trade' },
    sic:   { code: '5399',   title: 'Retail Stores, NEC' },
  },
  'Manufacturing':               {
    naics: { code: '339999', title: 'All Other Miscellaneous Manufacturing', sector: '31-33 - Manufacturing' },
    sic:   { code: '3999',   title: 'Manufacturing Industries, NEC' },
  },
  'Media & Entertainment':       {
    naics: { code: '512110', title: 'Motion Picture & Video Production', sector: '51 - Information' },
    sic:   { code: '7812',   title: 'Motion Picture & Video Tape Production' },
  },
  'Other':                       null,
};

const emptyForm = {
  name: '', legal_name: '', dba_name: '', entity_type: '', ein: '', formation_state: '',
  industry: '', naics_code: '', naics_title: '', sic_code: '', sic_title: '',
  address: '', phone: '', website: '',
  applicant_name: '', applicant_title: '', applicant_phone: '', applicant_email: '',
  employee_count: '', opening_date: '', logo: '', description: '',
};

export default function CompaniesPage() {
  const [companies, setCompanies] = useState([]);
  const [projects,  setProjects]  = useState([]);
  const [expanded,  setExpanded]  = useState(new Set());
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form,      setForm]      = useState(emptyForm);
  const [saving,    setSaving]    = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState('');
  const [showUpgrade, setShowUpgrade] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [companiesRes, projectsRes] = await Promise.all([ getMyCompanies(), getProjects() ]);
      setCompanies(companiesRes.data || []);
      setProjects(projectsRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const toggleExpanded = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const projectsFor = (companyId) => projects.filter(p => String(p.meta?.gpf_company_id) === String(companyId));

  const openNew = () => { setForm(emptyForm); setEditingId(null); setError(''); setShowForm(true); };

  const openEdit = async (company) => {
    try {
      const res = await api.get(`/wp/v2/companies/${company.id}`);
      const m = res.data.meta || {};
      setForm({
        name: res.data.title?.rendered || '',
        legal_name: m.gpf_legal_name || '', dba_name: m.gpf_dba_name || '',
        entity_type: m.gpf_entity_type || '', ein: m.gpf_ein || '', formation_state: m.gpf_formation_state || '',
        industry: m.gpf_industry || '', naics_code: m.gpf_naics_code || '', naics_title: m.gpf_naics_title || '',
        sic_code: m.gpf_sic_code || '', sic_title: m.gpf_sic_title || '',
        address: m.gpf_address || '', phone: m.gpf_phone || '', website: m.gpf_website || '',
        applicant_name: m.gpf_applicant_name || '', applicant_title: m.gpf_applicant_title || '',
        applicant_phone: m.gpf_applicant_phone || '', applicant_email: m.gpf_applicant_email || '',
        employee_count: m.gpf_employee_count || '', opening_date: m.gpf_opening_date || '',
        logo: m.gpf_logo_url || '',
        description: m.gpf_description || '',
      });
      setEditingId(company.id);
      setError('');
      setShowForm(true);
    } catch (e) { console.error(e); }
  };

  const handleIndustryChange = (industry) => {
    const match = NAICS_MAP[industry];
    setForm(prev => ({
      ...prev,
      industry,
      naics_code:  match?.naics.code  || '',
      naics_title: match?.naics.title || '',
      sic_code:    match?.sic.code    || '',
      sic_title:   match?.sic.title   || '',
    }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !editingId) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const res = await api.post(`/gpf/v1/companies/${editingId}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(prev => ({ ...prev, logo: res.data.url }));
    } catch (e) { setError('Logo upload failed.'); }
    finally { setUploading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Company name is required.'); return; }
    setSaving(true);
    setError('');
    const meta = {
      gpf_legal_name: form.legal_name, gpf_dba_name: form.dba_name, gpf_entity_type: form.entity_type,
      gpf_ein: form.ein, gpf_formation_state: form.formation_state, gpf_industry: form.industry,
      gpf_naics_code: form.naics_code, gpf_naics_title: form.naics_title,
      gpf_sic_code: form.sic_code, gpf_sic_title: form.sic_title,
      gpf_address: form.address, gpf_phone: form.phone, gpf_website: form.website,
      gpf_applicant_name: form.applicant_name, gpf_applicant_title: form.applicant_title,
      gpf_applicant_phone: form.applicant_phone, gpf_applicant_email: form.applicant_email,
      gpf_employee_count: form.employee_count, gpf_opening_date: form.opening_date,
      gpf_description: form.description,
    };
    try {
      if (editingId) {
        await updateCompany(editingId, { name: form.name, meta });
      } else {
        await createCompany({ name: form.name, meta });
      }
      setShowForm(false);
      fetchAll();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to save company.';
      setError(err.response?.status === 403 ? `⚠️ ${msg}` : msg);
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This will also permanently delete all of its projects and tasks.`)) return;
    try {
      await deleteCompany(id);
      setCompanies(companies.filter(c => c.id !== id));
    } catch (e) { alert('Failed to delete company.'); }
  };

  if (loading) return <Layout><p style={{ color: '#64748b', padding: 32 }}>Loading companies...</p></Layout>;

  return (
    <Layout>
      <div style={styles.page}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Companies</h1>
            <p style={styles.subtitle}>Manage multiple businesses under one account — {companies.length} compan{companies.length !== 1 ? 'ies' : 'y'}</p>
          </div>
          <button style={styles.primaryBtn} onClick={openNew}>+ New Company</button>
        </div>

        {companies.length === 0 ? (
          <div style={styles.empty}>
            <p style={{ fontSize: 15, color: '#64748b', margin: '0 0 16px' }}>No companies yet. If you manage more than one business, add each one here.</p>
            <button style={styles.primaryBtn} onClick={openNew}>+ Add Your First Company</button>
          </div>
        ) : (
          <div style={styles.list}>
            {companies.map(c => {
              const compProjects = projectsFor(c.id);
              const isOpen = expanded.has(c.id);
              return (
                <div key={c.id} style={styles.companyBlock}>
                  <div style={styles.companyRow}>
                    <button style={styles.expandBtn} onClick={() => toggleExpanded(c.id)} title={isOpen ? 'Collapse' : 'Expand to see projects'}>
                      {isOpen ? '▾' : '▸'}
                    </button>
                    {c.logo ? (
                      <img src={c.logo} alt="" style={styles.logoThumb} />
                    ) : (
                      <div style={styles.logoFallback}>🏢</div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3 style={styles.companyName}>{c.name}</h3>
                      {c.description && <p style={styles.description}>{c.description}</p>}
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        {c.naics && <span style={styles.metaTag}>{c.naics}</span>}
                        {c.ein && <span style={styles.metaTag}>EIN: {c.ein}</span>}
                      </div>
                    </div>
                    <span style={styles.projectBadge} onClick={() => toggleExpanded(c.id)}>
                      ✓ {compProjects.length} project{compProjects.length !== 1 ? 's' : ''}
                    </span>
                    <div style={styles.companyActions}>
                      <button style={styles.cardBtn} onClick={() => openEdit(c)}>Edit</button>
                      <button style={{ ...styles.cardBtn, color: '#dc2626' }} onClick={() => handleDelete(c.id, c.name)}>Delete</button>
                    </div>
                  </div>

                  {isOpen && (
                    <div style={styles.projectsPanel}>
                      {compProjects.length === 0 ? (
                        <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>No projects yet for this company.</p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {compProjects.map(p => (
                            <a key={p.id} href="/projects" style={styles.projectRow}>
                              <span style={{ fontWeight: 600 }}>{p.title?.rendered || 'Untitled'}</span>
                              <span style={{ fontSize: 12, color: '#94a3b8' }}>{p.meta?.gpf_status || 'active'}</span>
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {showForm && (
          <div style={styles.modalOverlay}>
            <div style={styles.modal}>
              <h2 style={styles.modalTitle}>{editingId ? 'Edit Company' : 'New Company'}</h2>
              {error && <p style={styles.error}>{error}</p>}

              {editingId && (
                <div style={styles.logoSection}>
                  {form.logo ? (
                    <img src={form.logo} alt="" style={styles.logoPreview} />
                  ) : (
                    <div style={styles.logoPreviewFallback}>🏢</div>
                  )}
                  <div>
                    <label style={styles.uploadBtn}>
                      {uploading ? 'Uploading...' : form.logo ? 'Change Logo' : 'Upload Logo'}
                      <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoUpload} disabled={uploading} />
                    </label>
                    <p style={{ fontSize: 11, color: '#94a3b8', margin: '6px 0 0' }}>PNG or JPG, square works best</p>
                  </div>
                </div>
              )}
              {!editingId && (
                <p style={{ fontSize: 12, color: '#94a3b8', margin: '0 0 14px' }}>💡 You can upload a logo once the company is created — save it first, then reopen to edit.</p>
              )}

              <form onSubmit={handleSubmit} style={styles.form}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={styles.label}>Company Name *</label>
                  <input style={styles.input} value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Acme Coffee Co." autoFocus />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={styles.label}>Brief Description</label>
                  <textarea
                    style={{ ...styles.input, height: 60, resize: 'vertical' }}
                    value={form.description}
                    onChange={e => setForm({...form, description: e.target.value})}
                    placeholder="One or two sentences about what this business does"
                    maxLength={200}
                  />
                </div>
                <div>
                  <label style={styles.label}>Legal Business Name</label>
                  <input style={styles.input} value={form.legal_name} onChange={e => setForm({...form, legal_name: e.target.value})} />
                </div>
                <div>
                  <label style={styles.label}>DBA / Trade Name</label>
                  <input style={styles.input} value={form.dba_name} onChange={e => setForm({...form, dba_name: e.target.value})} />
                </div>
                <div>
                  <label style={styles.label}>Entity Type</label>
                  <select style={styles.input} value={form.entity_type} onChange={e => setForm({...form, entity_type: e.target.value})}>
                    <option value="">Select type...</option>
                    <option value="sole_proprietorship">Sole Proprietorship</option>
                    <option value="llc">LLC</option>
                    <option value="corporation">Corporation</option>
                    <option value="s_corp">S-Corporation</option>
                    <option value="partnership">Partnership</option>
                    <option value="nonprofit">Nonprofit</option>
                  </select>
                </div>
                <div>
                  <label style={styles.label}>EIN</label>
                  <input style={styles.input} value={form.ein} onChange={e => setForm({...form, ein: e.target.value})} placeholder="XX-XXXXXXX" />
                </div>
                <div>
                  <label style={styles.label}>State of Formation</label>
                  <input style={styles.input} value={form.formation_state} onChange={e => setForm({...form, formation_state: e.target.value})} />
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={styles.label}>Industry</label>
                  <select style={styles.input} value={form.industry} onChange={e => handleIndustryChange(e.target.value)}>
                    <option value="">Select industry...</option>
                    {Object.keys(NAICS_MAP).map(ind => <option key={ind} value={ind}>{ind}</option>)}
                  </select>
                </div>
                {form.naics_code && (
                  <div style={{ gridColumn: '1 / -1', background: '#eff6ff', borderRadius: 8, padding: '10px 14px', fontSize: 12.5, color: '#1e3a8a', display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                    <span><strong>NAICS {form.naics_code}</strong> — {form.naics_title}</span>
                    {form.sic_code && <span><strong>SIC {form.sic_code}</strong> — {form.sic_title}</span>}
                  </div>
                )}

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={styles.label}>Address</label>
                  <input style={styles.input} value={form.address} onChange={e => setForm({...form, address: e.target.value})} />
                </div>
                <div>
                  <label style={styles.label}>Phone</label>
                  <input style={styles.input} value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
                </div>
                <div>
                  <label style={styles.label}>Website</label>
                  <input style={styles.input} value={form.website} onChange={e => setForm({...form, website: e.target.value})} />
                </div>
                <div>
                  <label style={styles.label}>Primary Applicant Name</label>
                  <input style={styles.input} value={form.applicant_name} onChange={e => setForm({...form, applicant_name: e.target.value})} />
                </div>
                <div>
                  <label style={styles.label}>Applicant Email</label>
                  <input style={styles.input} type="email" value={form.applicant_email} onChange={e => setForm({...form, applicant_email: e.target.value})} />
                </div>
                <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
                  <button type="button" style={styles.cancelBtn} onClick={() => setShowForm(false)}>Cancel</button>
                  <button type="submit" style={styles.primaryBtn} disabled={saving}>{saving ? 'Saving...' : editingId ? 'Save Changes' : 'Create Company'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showUpgrade && (
          <div style={styles.modalOverlay}>
            <div style={{ ...styles.modal, maxWidth: 400, textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🏢</div>
              <h2 style={{ fontSize: 19, fontWeight: 800, margin: '0 0 10px' }}>Company Limit Reached</h2>
              <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.7, margin: '0 0 24px' }}>
                Your current plan limits how many companies you can add. Upgrade to manage more businesses from one account.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <a href="/workspace?upgrade=1" style={{ padding: '12px 24px', background: '#fb923c', color: 'white', borderRadius: 10, fontWeight: 700, fontSize: 15, textDecoration: 'none' }}>Upgrade Plan →</a>
                <button style={styles.cancelBtn} onClick={() => setShowUpgrade(false)}>Maybe Later</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

const styles = {
  page:        { padding: 32, maxWidth: 1100, margin: '0 auto' },
  header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 },
  title:       { fontSize: 26, fontWeight: 800, margin: 0 },
  subtitle:    { fontSize: 14, color: '#64748b', margin: '4px 0 0' },
  primaryBtn:  { padding: '11px 22px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  cancelBtn:   { padding: '11px 22px', background: 'white', color: '#374151', border: '1.5px solid #e2e8f0', borderRadius: 9, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  empty:       { background: 'white', borderRadius: 12, padding: '60px 24px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  list:        { display: 'flex', flexDirection: 'column', gap: 12 },
  companyBlock:{ background: 'white', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' },
  companyRow:  { display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px' },
  expandBtn:   { width: 22, height: 22, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14, color: '#64748b', flexShrink: 0 },
  logoThumb:   { width: 40, height: 40, borderRadius: 8, objectFit: 'cover', flexShrink: 0 },
  logoFallback:{ width: 40, height: 40, borderRadius: 8, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 },
  companyName: { fontSize: 15, fontWeight: 700, margin: '0 0 4px' },
  description: { fontSize: 12.5, color: '#64748b', margin: '0 0 6px', lineHeight: 1.5 },
  metaTag:     { fontSize: 11.5, color: '#64748b' },
  projectBadge:{ fontSize: 12, fontWeight: 600, color: '#2563eb', background: '#eff6ff', padding: '4px 12px', borderRadius: 99, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
  companyActions: { display: 'flex', gap: 8, flexShrink: 0 },
  cardBtn:     { padding: '6px 14px', border: '1.5px solid #e2e8f0', borderRadius: 7, background: 'white', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', color: '#374151' },
  projectsPanel:{ background: '#f8fafc', borderTop: '1px solid #f1f5f9', padding: '14px 20px 14px 56px' },
  projectRow:  { display: 'flex', justifyContent: 'space-between', padding: '9px 12px', background: 'white', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, textDecoration: 'none', color: '#0f172a' },
  modalOverlay:{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 },
  modal:       { background: 'white', borderRadius: 16, padding: 32, maxWidth: 640, width: '100%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' },
  modalTitle:  { fontSize: 19, fontWeight: 800, margin: '0 0 18px' },
  logoSection: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, paddingBottom: 20, borderBottom: '1px solid #f1f5f9' },
  logoPreview: { width: 64, height: 64, borderRadius: 12, objectFit: 'cover', flexShrink: 0 },
  logoPreviewFallback: { width: 64, height: 64, borderRadius: 12, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, flexShrink: 0 },
  uploadBtn:   { display: 'inline-block', padding: '8px 16px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#374151' },
  form:        { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
  label:       { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 },
  input:       { width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
  error:       { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#dc2626', marginBottom: 12 },
};